<template>
  <component
    :is="draggableComponent"
    :id="`draggable-level-${level}`"
    class="text-left"
    :class="{ 'pb-2': level > 1, 'px-6': level > 1, 'tw:p-0': level === 1 }"
    tag="ul"
    @add="onAdd"
    @update="onUpdate"
    :list="items"
    :group="{ name: 'elements' }"
    :scrollSensitivity="200"
    :move="onMove"
    filter="button"
    v-on="$listeners"
    v-bind="{
      ...(multi && level !== 1
        ? { multiDrag: true, ghostClass: 'draggable-selected', selectedClass: 'draggable-selected' }
        : {}),
      ...(hasDragHandler ? { handle: '.handle' } : {}),
    }"
  >
    <li
      v-for="(element, idx) in items"
      :key="`${parentKey}-${element.id || element.id === 0 ? element.id : idx}`"
      :id="idx"
      :class="element[childItemsKey] ? classesGroup : classesElement"
      v-on="{
        ...(onElementClickAction && !element[childItemsKey] ? { click: () => onElementClickAction(element) } : {}),
      }"
    >
      <slot
        name="listElement"
        v-bind="{
          ...element,
          parent,
          idx,
          level,
          parentKey,
          elementKey: `${parentKey}-${idx}`,
          dotsItems: element.dotsItems || (element[childItemsKey] ? groupsDotsItems : elementsDotsItems) || [],
          disabled,
          loading,
        }"
      >
        <draggable-list-item
          v-bind="{
            ...element,
            parent,
            level,
            parentKey,
            hideableGroups,
            elementKey: `${parentKey}-${idx}`,
            dotsItems: element.dotsItems || (element[childItemsKey] ? groupsDotsItems : elementsDotsItems) || [],
            disabled,
            disableBorders,
            loading,
            titleKey: itemTitleKey,
            childItemsKey: childItemsKey,
            hasDragHandler: hasDragHandler && !element.disableDraggable,
            disableDraggable: disableDraggable && !element.disableDraggable,
            itemTooltips: itemTooltips,
          }"
          v-on="$listeners"
          @toggleHideGroup="toggleHideGroup({ ...element, idx })"
        >
          <template v-for="(value, key) in $scopedSlots" #[key]="scope">
            <slot :name="key" v-bind="scope" />
          </template>
        </draggable-list-item>
      </slot>
      <draggable-list-draggable
        v-if="element[childItemsKey]"
        v-show="hideableGroups ? !element.isHidden : true"
        :parent="$_filterObjectKeys(element, { notAllowedKeys: [childItemsKey] })"
        :items="element[childItemsKey]"
        :allow-elements-on-main-level="allowElementsOnMainLevel"
        :allow-one-element-on-main-level="allowOneElementOnMainLevel"
        :allow-nested-groups="allowNestedGroups"
        :disabled="disabled"
        :hideable-groups="hideableGroups"
        :toggle-hide-group-function="toggleHideGroupFunction"
        :disable-draggable="disableDraggable"
        :disable-borders="disableBorders"
        :element-classes="elementClasses"
        :on-element-click-action="onElementClickAction"
        :has-drag-handler="hasDragHandler"
        :elements-dots-items="elementsDotsItems"
        :groups-dots-items="groupsDotsItems"
        :item-title-key="itemTitleKey"
        :level="level + 1"
        :parent-key="`${parentKey}-${idx}`"
        :child-items-key="childItemsKey"
        :loading="loading"
        :multi="multi"
        :item-tooltips="itemTooltips"
        v-on="$listeners"
      >
        <template v-for="(value, key) in $scopedSlots" #[key]="scope">
          <slot :name="key" v-bind="scope" />
        </template>
      </draggable-list-draggable>
    </li>
  </component>
</template>
<script>
import draggable from 'vuedraggable-multi';
import DraggableListItem from '@/components/DraggableListItem';
export default {
  name: 'DraggableListDraggable',
  components: { DraggableListItem },
  props: {
    allowElementsOnMainLevel: {
      type: Boolean,
      default: false,
    },
    allowOneElementOnMainLevel: {
      type: Boolean,
      default: false,
    },
    hideableGroups: {
      type: Boolean,
      default: false,
    },
    toggleHideGroupFunction: {
      type: Function,
    },
    allowNestedGroups: {
      type: Boolean,
      default: false,
    },
    childItemsKey: {
      type: String,
      default: 'items',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    parent: {
      type: Object,
    },
    items: {
      type: Array,
      default: () => {
        return [];
      },
    },
    elementsDotsItems: {
      type: Array,
    },
    groupsDotsItems: {
      type: Array,
    },
    itemTitleKey: {
      type: String,
      default: 'name',
    },
    level: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    multi: {
      type: Boolean,
      default: false,
    },
    disableDraggable: {
      type: Boolean,
      default: false,
    },
    disableBorders: {
      type: Boolean,
      default: false,
    },
    parentKey: {
      type: [String, Number],
      default: '0',
    },
    elementClasses: {
      type: Array,
      default: () => {
        return [];
      },
    },
    onElementClickAction: {
      type: Function,
    },
    hasDragHandler: {
      type: Boolean,
      default: false,
    },
    itemTooltips: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    classesGroup() {
      return [
        ...(this.disableBorders ? [...(this.level > 1 ? ['pl-6'] : ['mb-2'])] : ['li--element-border']),
        'li-group',
      ];
    },
    classesElement() {
      return [
        ...this.elementClasses,
        ...(this.level === 1
          ? [...(this.disableBorders ? ['mb-2'] : ['li--element-border'])]
          : ['margin--bottom', 'border']),
      ];
    },
    draggableComponent() {
      return this.disableDraggable ? 'ul' : draggable;
    },
    pinnedElementsLength() {
      return this.items?.filter(el => el.pinned).length || 0;
    },
  },
  methods: {
    onAdd(e) {
      this.$emit('elementAdded', e);
    },
    onUpdate(e) {
      this.$emit('elementUpdated', e);
    },
    onMove(e) {
      if (e.draggedContext.futureIndex + 1 <= this.pinnedElementsLength) return false;
      if (this.loading) return false;
      const isSomeElementGroup = Array.isArray(e.draggedContext)
        ? e.draggedContext.some(x => x.element[this.childItemsKey])
        : e.draggedContext.element[this.childItemsKey];

      if (!this.allowNestedGroups && isSomeElementGroup && e.to.id !== 'draggable-level-1') {
        return false;
      }
      if (!this.allowElementsOnMainLevel && !isSomeElementGroup && e.to.id === 'draggable-level-1') {
        return false;
      }
      if (this.allowOneElementOnMainLevel && e.to.id === 'draggable-level-1') return false;
    },
    toggleHideGroup(element) {
      if (this.toggleHideGroupFunction) this.toggleHideGroupFunction(element);
      else {
        if (element.groupName && element.id) {
          const hiddenGroups = JSON.parse(localStorage.getItem(`${element.groupName}-hidden-groups`) || '{}');
          localStorage.setItem(
            `${element.groupName}-hidden-groups`,
            JSON.stringify({
              ...hiddenGroups,
              [element.id]: !element.isHidden,
            })
          );
        }
      }
      this.$set(this.items[element.idx], 'isHidden', !element.isHidden);
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped lang="scss">
ul {
  list-style: none;
  padding-left: 0;
}
.margin--bottom {
  &:not(:last-child) {
    margin-bottom: 4px;
  }
}
.li--element-border {
  @include border(top);
  @include border(right);
  @include border(left);
  &:last-child {
    @include border(bottom);
  }
}
.li-group {
  background-color: #f9fafb !important;
}
::v-deep .draggable-selected {
  @include draggableSelectedElement();
}
</style>
